<script setup lang="ts">
import type { ContentProductPackage } from '~/models/Content/ContentProductPackage'
import type { ContentProductHeader } from '~/models/Content/ContentProductHeader'
import type { ContentPackage } from '~/models/Content/ContentPackage'
import type { ContentFolder } from '~/models/Content/ContentFolder'
import { computed } from 'vue'
import useFilterStore from '~/stores/filter'
import { PendoFeature } from '~/models/Pendo'
import { useStaticProduct } from '~/composables/useStaticProduct'
import { useHeaderChildrenQuery } from '~/composables/useHeader'
import useAccordion from '~/composables/useAccordion'
import RichTextRenderer from '~/components/utils/RichTextRenderer.vue'
import AuSkeletonWrapper from '~/components/ui/AuSkeletonWrapper.vue'
import AuSkeleton from '~/components/ui/AuSkeleton.vue'
import HeaderPreviewAccordion from '~/components/subject/HeaderPreviewAccordion.vue'
import ColorCard from '~/components/cards/ColorCard.vue'

const { header, section, style, index, hash, gradeFilter } = defineProps<{
  header: ContentProductHeader | ContentProductPackage | ContentFolder
  section: ContentPackage
  isCollapsible: boolean
  style: string
  index: number
  hash: string
  gradeFilter?: boolean
}>()

const { gradeFilterFunc } = useFilterStore()
const { Terminprover } = useStaticProduct()
const { addAccordion } = useAccordion()

const { data, isLoading } = useHeaderChildrenQuery(computed(() => section.mainLocationId), {
  sortField: computed(() => section.sortField),
  sortOrder: computed(() => section.sortOrder),
})

const children = computed(() => (gradeFilter ? data.value?.filter(gradeFilterFunc) : data.value) ?? [])

const tracking = computed(() => section.pathString.startsWith(Terminprover.aunivers.pathString!)
  ? { 'data-pendo': PendoFeature.TermExam.NavigateTask }
  : {}
)
</script>

<template>
  <section
    v-if="isLoading || children.length > 0"
    :style="style"
  >
    <AuSkeletonWrapper
      v-if="isLoading"
      class="max-w-screen-au mx-auto space-y-4 px-4 py-8 sm:px-8"
    >
      <AuSkeleton
        height="36px"
        width="120px"
      />
      <ul class="xs:grid-cols-2 grid grid-cols-1 gap-8 md:grid-cols-3 lg:grid-cols-4">
        <AuSkeleton
          v-for="i in 4"
          :key="i"
          height="280px"
        />
      </ul>
    </AuSkeletonWrapper>
    <HeaderPreviewAccordion
      v-else-if="isCollapsible"
      :id="`${index + 1}`"
      :header="header"
      :section="section"
      :open="`#${index + 1}` === hash"
    >
      <ul class="xs:grid-cols-2 grid grid-cols-1 gap-8 md:grid-cols-3 lg:grid-cols-4">
        <li
          v-for="(childContent, childIndex) in children"
          :key="childContent.locationId"
          v-bind="tracking"
        >
          <ColorCard
            :resource="childContent"
            :loading="childIndex === 0 ? 'eager' : 'lazy'"
            @click="addAccordion(header.locationId, section.locationId)"
          />
        </li>
      </ul>
    </HeaderPreviewAccordion>
    <div
      v-else
      :id="`${index + 1}`"
      class="max-w-screen-au mx-auto space-y-8 px-4 py-12 sm:px-8"
    >
      <span class="flex flex-col space-y-4">
        <h2
          class="font-calistoga fluid-text-2xl"
          v-text="section.title"
        />
        <div v-if="section.intro" class="max-w-prose text-xl font-semibold">
          <RichTextRenderer :text="section.intro" />
        </div>
      </span>
      <ul class="xs:grid-cols-2 grid grid-cols-1 gap-8 md:grid-cols-3 lg:grid-cols-4">
        <li
          v-for="(childContent, childIndex) in children"
          :key="childContent.locationId"
          v-bind="tracking"
        >
          <ColorCard
            :resource="childContent"
            :loading="childIndex === 0 ? 'eager' : 'lazy'"
          />
        </li>
      </ul>
    </div>
  </section>
</template>
